import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { services } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  ImageIcon, Loader2, RotateCcw, Upload,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MAX_BYTES = 6 * 1024 * 1024;
const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1666871701015-6fab56ecd4c8?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1633536704679-de310869515b?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1732896065182-ad7d8fdc766f?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1772291899685-0b33cb04df45?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1762604462465-76720d039e9f?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1726499180628-7b0975170df8?crop=entropy&cs=srgb&fm=jpg&q=85',
];

/**
 * Per-card image editor for the public /services page.
 *
 * The page exposes exactly six service cards (electrical, household,
 * toys-gadgets, clothing-textiles, bicycles-mechanical, tool-sharpening).
 * Each card has a fixed title + description (managed via EditableText
 * on the public page); this editor only handles the IMAGE for each
 * card, with a per-card "Replace" picker and a "Revert to default"
 * action that wipes the customisation.
 */
export default function ServiceImageEditor() {
  const { getAuthHeader } = useAuth();
  const [customByslug, setCustomBySlug] = useState({});
  const [loading, setLoading] = useState(true);
  // Per-slug "is currently uploading" flag — used to disable the card's
  // controls and swap the icon for a spinner.
  const [busySlug, setBusySlug] = useState(null);
  // Hidden <input type="file"> per slug; ref kept in a map so the
  // "Replace image" button can trigger the picker programmatically.
  const fileRefs = useRef({});

  useEffect(() => { fetchImages(); }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/service-images/public`);
      const map = {};
      (r.data || []).forEach((row) => { if (row.slug) map[row.slug] = row; });
      setCustomBySlug(map);
    } catch {
      toast.error('Failed to load service images');
    } finally {
      setLoading(false);
    }
  };

  const onPick = (slug) => {
    const el = fileRefs.current[slug];
    if (el) el.click();
  };

  const onFile = async (slug, file) => {
    if (!file) return;
    if (!ALLOWED_MIME.has(file.type)) {
      toast.error('Unsupported file type. Use JPG, PNG, WebP or GIF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Max 6 MB.`);
      return;
    }
    setBusySlug(slug);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.put(
        `${API}/service-images/${slug}`,
        fd,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setCustomBySlug((m) => ({ ...m, [slug]: res.data }));
      toast.success('Image updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusySlug(null);
      // Clear the input so picking the same file again still fires onChange.
      const el = fileRefs.current[slug];
      if (el) el.value = '';
    }
  };

  const onRevert = async (slug) => {
    if (!window.confirm('Revert this card to the default image?')) return;
    setBusySlug(slug);
    try {
      await axios.delete(
        `${API}/service-images/${slug}`,
        { headers: getAuthHeader() },
      );
      setCustomBySlug((m) => {
        const next = { ...m };
        delete next[slug];
        return next;
      });
      toast.success('Reverted to default image');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert');
    } finally {
      setBusySlug(null);
    }
  };

  const imageFor = (slug, fallbackIdx) =>
    customByslug[slug]?.url || FALLBACK_IMAGES[fallbackIdx] || FALLBACK_IMAGES[0];

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-600" data-testid="service-images-loading">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2 text-[#E87B6A]" />
        Loading service images…
      </div>
    );
  }

  return (
    <div data-testid="service-images-editor">
      <div className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-900">
        <ImageIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          <strong>Single source of truth for the /services page.</strong>{' '}
          Each card below maps to one image on the public Services page.
          Replace any image individually — the title and description stay
          the same. Up to 6&nbsp;MB per file (JPG, PNG, WebP, GIF).
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((s, idx) => {
          const custom = !!customByslug[s.slug];
          const src = imageFor(s.slug, idx);
          const isBusy = busySlug === s.slug;
          return (
            <Card
              key={s.slug}
              data-testid={`service-image-card-${s.slug}`}
              className="border-2 border-gray-200 overflow-hidden"
            >
              <div className="relative h-44 bg-gray-100">
                <img
                  src={src}
                  alt={s.title}
                  className="w-full h-full object-cover"
                  data-testid={`service-image-thumb-${s.slug}`}
                />
                <span
                  className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded ${
                    custom
                      ? 'bg-[#193460] text-white'
                      : 'bg-white/90 text-gray-600 border border-gray-300'
                  }`}
                  data-testid={`service-image-source-${s.slug}`}
                >
                  {custom ? 'Custom' : 'Default'}
                </span>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#193460] text-base leading-tight">
                  {s.title}
                </h3>
                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                  {s.description}
                </p>

                <input
                  ref={(el) => { fileRefs.current[s.slug] = el; }}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  data-testid={`service-image-input-${s.slug}`}
                  onChange={(e) => onFile(s.slug, e.target.files?.[0])}
                />

                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => onPick(s.slug)}
                    data-testid={`service-image-replace-${s.slug}`}
                    className="bg-[#E87B6A] hover:bg-[#d66a59] text-white flex-1"
                  >
                    {isBusy ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    Replace image
                  </Button>
                  {custom && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isBusy}
                      onClick={() => onRevert(s.slug)}
                      data-testid={`service-image-revert-${s.slug}`}
                      className="border-gray-300"
                      title="Revert to default image"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
